package uk.co.nickthecoder.kyd.appearance

import uk.co.nickthecoder.glok.backend.Backend
import uk.co.nickthecoder.glok.property.boilerplate.ColorProperty
import uk.co.nickthecoder.glok.scene.Color
import uk.co.nickthecoder.glok.util.Matrix
import uk.co.nickthecoder.kyd.Actor
import uk.co.nickthecoder.kyd.util.Vector2

/**
 * Responsible for drawing an [Actor].
 *
 * An [Actor] can change appearance over time.
 */
interface Appearance : WithAppearance {

    /**
     * The actor's transformation has already been applied to [Backend] (using [Backend.transform]).
     * So we do NOT need to use [Actor.position], [Actor.scale] or [Actor.angleRadians].
     */
    fun draw(modelMatrix: Matrix)

    /**
     * Returns a list of local points which describes the bounding polygon.
     * `local` means the points are unrelated to the Actor's position, angle or scale.
     */
    fun localCorners(): List<Vector2>

    /**
     * Returns two corners of a bounding rectangle in local coordinates.
     */
    fun localBounds(): Pair<Vector2, Vector2>

    /**
     * Given a point in local coordinates, is it within the bounding box of this [Appearance]?
     *
     * @param localPoint A point local relative to this appearance (i.e. unrelated to the Actor's position, angle or scale).
     */
    fun containsLocalPoint(localPoint: Vector2): Boolean

    override fun appearanceForActor(actor: Actor) = this

    override fun appearance() = this

    /**
     * Most appearances are [TintedAppearance], but checking if an appearance is a [TintedAppearance] can be onerous.
     * This gives the illusion that all appearances have a tint. For those without a tint, the value will be
     * [Color.WHITE], and setting this value will do nothing.
     */
    var tintIfAvailable: Color
        get() = if (this is TintedAppearance) {
            tint
        } else if (this is FlipBookAnimation) {
            this.currentAppearance.tintIfAvailable
        } else {
            Color.WHITE
        }
        set(value) {
            if (this is TintedAppearance) {
                tint = value
            } else if (this is FlipBookAnimation) {
                this.currentAppearance.tintIfAvailable = value
            }
        }
}

interface TintedAppearance : Appearance {

    /**
     * When we draw, every pixel is multiplied by this `tint` color.
     * The default tint is fully-opaque white (1,1,1,1), therefore, the final image doesn't change color
     * (or opacity).
     *
     * However, if we change the tint's opacity to 0.5, then the final image will be semi-transparent.
     * This is the most common use of tint - to fade in/out.
     *
     * Using a tint where the red, green or blue values are not 1 is less often used.
     * If we have an appearance of a white circle, and apply a blue tint, the result is a blue circle.
     * However, if we start with a green circle, and apply a blue tint, we will see a BLACK circle.
     * The calculation is : `green (0, 1, 0, 1) * blue (0, 0, 1, 1) = black (0, 0, 0, 1)`.
     * Therefore, only apply colored tints to greyscale images (unless you want weird results).
     *
     * Suppose you have two teams of people, the `blue-coats` and the `red coats`. Create [Pose]s, but exclude
     * the coats. Now create another set of [Pose]s containing just the coats as _greyscale_ images.
     * You can then give each `person` a hat (see [Actor.children]) which is tinted blue or red.
     */
    val tintProperty: ColorProperty
    var tint: Color

}


/**
 * Draws nothing (invisible).
 */
object NoAppearance : Appearance {

    override fun draw(modelMatrix: Matrix) {
        // DO nothing
    }

    override fun localCorners(): List<Vector2> = emptyList()

    override fun localBounds() = NO_BOUNDS

    /**
     * @return false
     */
    override fun containsLocalPoint(localPoint: Vector2) = false

    override fun toString() = "NoAppearance"

    val NO_BOUNDS = Pair(Vector2.ZERO, Vector2.ZERO)
}
